import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  createBadgeAPI,
  getAllBadgesAPI,
  updateBadgeAPI,
} from "../../services/badges/badgeService";
import { handleError } from "../../utils/handleError";
import { setListBadges } from "../../redux/slice/badgesSlice";

const ModalBadge = ({ open, setOpen, initialData, isEdit, setLoading }) => {
  const [badgeData, setBadgeData] = useState({
    nameBadge: "",
    description: "",
    iconURL: "",
    type: "",
    goalValue: 0,
    membershipReq: "",
    isActive: true,
  });
  const { listMemberships } = useSelector((state) => state.membershipsReducer);
  const dispatch = useDispatch();

  // Tipos disponibles para el logro
  const badgeTypes = [
    { value: "distance", label: "Distancia" },
    { value: "routeComplete", label: "Ruta completa" },
    { value: "routeCreate", label: "Ruta creada" },
    { value: "invite", label: "Invitación" },
    { value: "streak", label: "Racha" },
    { value: "event", label: "Evento" },
  ];

  useEffect(() => {
    if (isEdit && initialData) {
      setBadgeData({
        ...initialData,
        isActive: initialData.isActive !== false,
      });
    } else {
      setBadgeData({
        nameBadge: "",
        description: "",
        iconURL: "",
        type: "",
        goalValue: 0,
        membershipReq: "",
        isActive: true,
      });
    }
  }, [open, initialData, isEdit, setLoading, dispatch, setOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBadgeData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      setLoading(true);
      if (isEdit) {
        // console.log("Datos del logro:", badgeData);
        await updateBadgeAPI(badgeData, initialData?._id);
        const responseBadges = await getAllBadgesAPI();
        dispatch(setListBadges({ listBadges: responseBadges }));
        toast.success("Logro actualizado correctamente.");
      } else {
        // console.log("Datos del logro:", badgeData);
        await createBadgeAPI(badgeData);
        const responseBadges = await getAllBadgesAPI();
        dispatch(setListBadges({ listBadges: responseBadges }));
        toast.success("Logro creado correctamente.");
      }
      setOpen(false);
      setLoading(false);
    } catch (error) {
      const { message } = handleError(error);
      setLoading(false);
      toast.error(
        `Error al ${isEdit ? "actualizar" : "crear"} el logro. \n ${message}`
      );
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Editar Logro" : "Crear Nuevo Logro"}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre del Logro"
                name="nameBadge"
                value={badgeData.nameBadge}
                onChange={handleChange}
                margin="normal"
                required
              />

              <TextField
                fullWidth
                label="Descripción"
                name="description"
                value={badgeData.description}
                onChange={handleChange}
                margin="normal"
                multiline
                rows={3}
                required
              />

              {/* Checkbox para estado activo/inactivo */}
              <FormControlLabel
                control={
                  <Checkbox
                    name="isActive"
                    checked={badgeData.isActive}
                    onChange={handleChange}
                    color="primary"
                  />
                }
                label={badgeData.isActive ? "Activo" : "Inactivo"}
                sx={{ mt: 2 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Tipo de Logro</InputLabel>
                <Select
                  name="type"
                  value={badgeData.type}
                  onChange={handleChange}
                  label="Tipo de Logro"
                >
                  {badgeTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label={
                  badgeData?.type === "distance"
                    ? "Kilometros requeridos"
                    : badgeData?.type === "routeComplete"
                      ? "Rutas completadas"
                      : badgeData?.type === "routeCreate"
                        ? "Total de rutas creadas"
                        : badgeData?.type === "event"
                          ? "Total de eventos"
                          : badgeData?.type === "streak"
                            ? "Rutas completadas en racha"
                            : "Valor meta"
                }
                name="goalValue"
                type="number"
                value={badgeData.goalValue}
                onChange={handleChange}
                margin="normal"
                required
                inputProps={{ min: 1 }}
              />

              <FormControl fullWidth margin="normal">
                <InputLabel>Membresía Requerida</InputLabel>
                <Select
                  name="membershipReq"
                  value={badgeData.membershipReq}
                  onChange={handleChange}
                  label="Membresía Requerida"
                  required
                >
                  {listMemberships.map((membership) => (
                    <MenuItem
                      key={membership?._id}
                      value={membership?.planName}
                    >
                      {membership?.planName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ display: "flex", gap: 2 }}>
          <Button onClick={handleClose} color="white" variant="outlined">
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{ backgroundColor: "#e7ef7d" }}
          >
            {isEdit ? "Actualizar" : "Crear"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ModalBadge;
